import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <button onClick={() => navigate(-1)} className="mb-8 text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-2">
          ← Volver
        </button>
        <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-gray-500 mb-10">Last updated: April 2, 2026</p>

        <div className="prose prose-gray max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-3">1. Introduction</h2>
            <p className="text-gray-600 leading-relaxed">AdPulse AI ("we", "our", or "us") operates adpulseia.com. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform. By using AdPulse AI, you agree to the collection and use of information in accordance with this policy.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">2. Information We Collect</h2>
            <p className="text-gray-600 leading-relaxed mb-3">We collect the following types of information:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-600">
              <li><strong>Account Information:</strong> Name, email address, and password when you register.</li>
              <li><strong>Business Information:</strong> Company name, website, and industry provided during onboarding.</li>
              <li><strong>Advertising Data:</strong> Campaign metrics, impressions, clicks, spend, and conversions imported from connected advertising platforms (Meta Ads, Google Ads, Google Analytics 4, Google Search Console).</li>
              <li><strong>Usage Data:</strong> Pages visited, features used, and interaction logs within the platform.</li>
              <li><strong>Payment Information:</strong> Processed securely through our payment provider. We do not store full credit card details.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">3. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-600">
              <li>To provide, operate, and maintain the AdPulse AI platform.</li>
              <li>To import and display your advertising campaign data in dashboards and reports.</li>
              <li>To generate AI-powered insights and automated reports.</li>
              <li>To process payments and manage subscriptions.</li>
              <li>To send transactional emails (reports, alerts, account notifications).</li>
              <li>To improve our services through analytics and feedback.</li>
              <li>To comply with legal obligations.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">4. Third-Party Integrations</h2>
            <p className="text-gray-600 leading-relaxed mb-3">AdPulse AI connects to the following third-party APIs with your explicit authorization:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-600">
              <li><strong>Meta Ads API:</strong> To import campaign performance data from Facebook and Instagram advertising accounts.</li>
              <li><strong>Google Ads API:</strong> To import campaign data, keyword performance, and ad metrics from Google Ads accounts.</li>
              <li><strong>Google Analytics 4 API:</strong> To import website traffic, user behavior, and conversion data.</li>
              <li><strong>Google Search Console API:</strong> To import organic search performance data.</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-3">We only access data that you explicitly authorize. You may revoke access at any time from your account settings or from the respective platform.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">5. Data Sharing and Disclosure</h2>
            <p className="text-gray-600 leading-relaxed">We do not sell, trade, or rent your personal information to third parties. We may share data with:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-600 mt-3">
              <li><strong>Service Providers:</strong> Supabase (database), payment processors, and email delivery services, solely to operate the platform.</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our legal rights.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">6. Data Retention</h2>
            <p className="text-gray-600 leading-relaxed">We retain your data for as long as your account is active. Upon account deletion, your data is permanently removed within 30 days, except where retention is required by law.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">7. Security</h2>
            <p className="text-gray-600 leading-relaxed">We implement industry-standard security measures including encryption in transit (HTTPS/TLS), encrypted storage, and access controls. However, no method of transmission over the internet is 100% secure.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">8. Your Rights</h2>
            <p className="text-gray-600 leading-relaxed mb-3">You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2 text-gray-600">
              <li>Access your personal data.</li>
              <li>Correct inaccurate data.</li>
              <li>Request deletion of your data.</li>
              <li>Withdraw consent for data processing.</li>
              <li>Export your data in a portable format.</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-3">To exercise these rights, contact us at privacy@adpulseia.com.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">9. Cookies</h2>
            <p className="text-gray-600 leading-relaxed">We use essential cookies to maintain your session and preferences. We do not use tracking or advertising cookies.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">10. Contact Us</h2>
            <p className="text-gray-600 leading-relaxed">If you have questions about this Privacy Policy, contact us at:</p>
            <div className="mt-3 p-4 bg-gray-50 rounded-lg">
              <p className="font-semibold">AdPulse AI</p>
              <p className="text-gray-600">Email: privacy@adpulseia.com</p>
              <p className="text-gray-600">Website: https://adpulseia.com</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
